import { useCallback, useEffect, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { ShopListWithScroll } from '~/components/ui/ShopListWithScroll';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, PedOverlaySection, WalletType } from '~/shared';
import { useBuy } from '../../hooks';
import {
	setActiveList,
	setActiveListIndex,
	setActiveStage,
	setMaxListIndex,
	setOpacity,
	setPrimaryColor,
	setSecondaryColor,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { CategoryImages, CategoryNames } from '../CategoryTab';
import { ListTabItem } from '../ListTabItem';

const ListTab = () => {
	const dispatch = useLayoutDispatch();
	const activeListIndex = useLayoutSelector(
		(state) => state.barberShop.activeListIndex,
	);
	const maxListIndex = useLayoutSelector(
		(state) => state.barberShop.maxListIndex,
	);
	const activeCategory = useLayoutSelector(
		(state) => state.barberShop.activeCategory,
	);
	const items = useObservable(rage.barberShop.getItems());
	const playerGender = useObservable(rage.player.getPlayerGender());
	const overlays = useObservable(
		rage.barberShop.getOverlayConfigsByGender(playerGender),
	);
	const hairColors = useObservable(rage.barberShop.getHairColors());
	const makeupColors = useObservable(rage.barberShop.getMakeupColors());
	const primaryColor = useLayoutSelector(
		(state) => state.barberShop.primaryColor,
	);
	const secondaryColor = useLayoutSelector(
		(state) => state.barberShop.secondaryColor,
	);
	const opacity = useLayoutSelector((state) => state.barberShop.opacity);

	const { handleBuy } = useBuy();

	const filteredItems = useMemo(
		() =>
			items?.filter(
				(item) =>
					item.shopSection === activeCategory &&
					overlays?.some(
						(ov) =>
							ov.section === item.overlaySection && ov.index === item.index,
					),
			),
		[items, activeCategory, overlays],
	);

	const itemsWithNames = useMemo(() => {
		return filteredItems?.map((item) => {
			const name = overlays?.find(
				(ov) => ov.section === item.overlaySection && ov.index === item.index,
			)?.name;
			return { ...item, name: name ?? '' };
		});
	}, [filteredItems, overlays]);

	const handleSelectItem = useCallback((index: number | null) => {
		dispatch(setActiveListIndex(index));
	}, []);

	const handleBack = useCallback(() => {
		dispatch(setActiveStage(StageTypes.Category));
	}, []);

	const getItemId = useCallback(
		(index: number) => {
			const item = filteredItems?.[index];
			return item ? `hair-${item.overlaySection}-${item.index}-${index}` : '';
		},
		[filteredItems],
	);

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (activeListIndex === null) {
			return;
		}

		handleBuy(WalletType.Cash);
	});

	useEffect(() => {
		dispatch(setMaxListIndex((itemsWithNames?.length ?? 0) - 1));
		dispatch(setActiveList(itemsWithNames));
	}, [itemsWithNames]);

	useEffect(() => {
		if (activeListIndex === null) {
			rage.barberShop
				.previewItem(null)
				.catch((err) => rage.notifications.showException(err));
			return;
		}

		const item = filteredItems?.[activeListIndex];

		const isHair =
			item?.overlaySection === PedOverlaySection.Hair ||
			item?.overlaySection === PedOverlaySection.FacialHair ||
			item?.overlaySection === PedOverlaySection.Eyebrows ||
			item?.overlaySection === PedOverlaySection.BodyHair;
		const palette = isHair ? hairColors : makeupColors;
		const fallbackColorIndex = palette?.[0]?.index;

		const overlaySection = item?.overlaySection;
		const overlayIndex = item?.index;
		const primaryIdx = palette?.find((c) => c.hex === primaryColor)?.index;
		const secondaryIdx = palette?.find((c) => c.hex === secondaryColor)?.index;

		if (
			overlaySection !== undefined &&
			overlayIndex !== undefined &&
			fallbackColorIndex !== undefined
		) {
			rage.barberShop
				.previewItem(
					overlaySection,
					overlayIndex,
					primaryIdx ?? fallbackColorIndex,
					secondaryIdx ?? fallbackColorIndex,
					opacity,
				)
				.catch((err) => rage.notifications.showException(err));
		} else {
			rage.barberShop
				.previewItem(null)
				.catch((err) => rage.notifications.showException(err));
		}
	}, [
		filteredItems,
		activeListIndex,
		activeCategory,
		primaryColor,
		secondaryColor,
		opacity,
	]);

	useEffectOnce(() => {
		return () => {
			rage.barberShop
				.previewItem(null)
				.catch((err) => rage.notifications.showException(err));
			dispatch(setPrimaryColor(0));
			dispatch(setSecondaryColor(0));
			dispatch(setOpacity(1));
		};
	});

	if (!activeCategory) {
		return;
	}

	return (
		<ShopListWithScroll
			icon={CategoryImages[activeCategory]}
			title={CategoryNames[activeCategory]}
			style={{ marginTop: 30 }}
			handleBack={handleBack}
			activeIndex={activeListIndex}
			maxIndex={maxListIndex}
			handleCheckInstall={() => false}
			handleSelect={handleSelectItem}
			height={567}
			enableAutoScroll={true}
			getItemId={getItemId}
		>
			{itemsWithNames?.map((item, index) => (
				<ListTabItem
					item={item}
					index={index}
					activeCategory={activeCategory}
					activeListIndex={activeListIndex}
					handleSelect={handleSelectItem}
					key={index}
				/>
			))}
		</ShopListWithScroll>
	);
};

export { ListTab };
